import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Sparkles, Shield, TrendingUp, CreditCard, Loader2 } from "lucide-react";
import request from "../utils/request";
import IPhoneMockupPair, { IPhoneHomeScreen } from "../components/IPhoneMockup";

const FEATURES = [
  {
    icon: Sparkles,
    title: "Delight Your Guests",
    description: "Offer private chefs, IV therapy, massages, and more — all bookable from their phone.",
  },
  {
    icon: TrendingUp,
    title: "Earn Passive Revenue",
    description: "Generate additional income from every service booked through your properties.",
  },
  {
    icon: Shield,
    title: "Fully Automated",
    description: "Service requests, payments, and scheduling are handled end to end — no extra work for you.",
  },
];

export default function ConciergeSubscriptionScreen() {
  const { hostInfo } = useSelector((state) => state.host);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchStatus = () => {
    if (!hostInfo?._id) return;
    setLoading(true);
    request
      .get(`/hosts/${hostInfo._id}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ conciergeEnabled: false, status: "none" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStatus();
  }, [hostInfo?._id]);

  const handleSubscribe = async (tier = "basic") => {
    setActionLoading(true);
    try {
      const { data } = await request.post(`/hosts/${hostInfo._id}/concierge/subscribe`, { tier });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to start checkout");
    } finally {
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    setActionLoading(true);
    try {
      const { data } = await request.post(`/hosts/${hostInfo._id}/concierge/portal`);
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to open billing portal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel? Your subscription will remain active until the end of the current billing period.")) return;
    setActionLoading(true);
    try {
      await request.post(`/hosts/${hostInfo._id}/concierge/cancel`);
      fetchStatus();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to cancel");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const isActive = status?.conciergeEnabled;
  const plan = status?.plans?.basic;
  const priceLabel = plan ? `$${(plan.priceInCents / 100).toFixed(0)}` : "$100";
  const statusParam = searchParams.get("status");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {statusParam === "success" && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <p className="text-sm text-emerald-800 font-medium">
            Subscription activated! Your guests can now book concierge services.
          </p>
        </motion.div>
      )}

      {isActive ? <ActiveView status={status} priceLabel={priceLabel} onManage={handleManage} onCancel={handleCancel} actionLoading={actionLoading} /> : <PromoView priceLabel={priceLabel} plan={plan} onSubscribe={handleSubscribe} actionLoading={actionLoading} />}
    </div>
  );
}

function PromoView({ priceLabel, plan, onSubscribe, actionLoading }) {
  return (
    <div className="space-y-12">
      {/* Hero */}
      <div className="relative bg-gray-900 rounded-3xl overflow-hidden">
        <div className="flex flex-col lg:flex-row items-center gap-8 p-8 lg:p-12">
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-gray-300" />
              <span className="text-xs font-semibold text-gray-300 tracking-wide uppercase">Premium Feature</span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3 tracking-tight">
              Concierge Services<br />for Your Guests
            </h1>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mb-6 leading-relaxed">
              Give your guests a luxury experience with on-demand services — private chefs, spa treatments, transportation, and more — all managed through a beautiful mobile app.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={() => onSubscribe("basic")}
                disabled={actionLoading}
                className="inline-flex items-center px-6 py-3 bg-white text-gray-900 text-sm font-bold rounded-full hover:bg-gray-100 transition-all disabled:opacity-60"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Subscribe Now — {priceLabel}/mo
              </button>
              <span className="text-sm text-gray-400">Cancel anytime</span>
            </div>
          </div>

          <div className="flex-shrink-0 hidden lg:block">
            <IPhoneMockupPair scale={0.65} />
          </div>
        </div>
      </div>

      {/* Features */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 text-center mb-8">Why Offer Concierge?</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl border-2 border-gray-900 p-8 text-center shadow-sm">
          <div className="inline-flex items-center gap-1 bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
            <CreditCard className="w-3.5 h-3.5" /> Monthly Plan
          </div>
          <div className="mb-4">
            <span className="text-5xl font-extrabold text-gray-900 tracking-tight">{priceLabel}</span>
            <span className="text-gray-500 text-lg">/mo</span>
          </div>
          <ul className="text-sm text-gray-600 space-y-2 mb-6">
            {(plan?.features || ["Guest concierge services", "Service request management", "In-app guest payments"]).map((f, i) => (
              <li key={i} className="flex items-center gap-2 justify-center">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <button
            onClick={() => onSubscribe("basic")}
            disabled={actionLoading}
            className="w-full py-3 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition-colors disabled:opacity-60"
          >
            {actionLoading ? "Redirecting..." : "Get Started"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveView({ status, priceLabel, onManage, onCancel, actionLoading }) {
  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Concierge Subscription</h1>
          <p className="text-gray-500 text-sm mt-1">Manage your concierge service subscription</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5" />
            {status.cancelAtPeriodEnd ? "Canceling" : "Active"}
          </span>
          {status.tier && status.tier !== "none" && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
              {status.tier}
            </span>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Plan Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Plan</span>
              <span className="text-sm font-medium text-gray-900">{priceLabel}/month</span>
            </div>
            {status.currentPeriodEnd && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">
                  {status.cancelAtPeriodEnd ? "Active until" : "Next billing"}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(status.currentPeriodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            )}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={onManage}
              disabled={actionLoading}
              className="flex-1 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-colors disabled:opacity-60"
            >
              {actionLoading ? "Loading..." : "Manage Billing"}
            </button>
            {!status.cancelAtPeriodEnd && (
              <button
                onClick={onCancel}
                disabled={actionLoading}
                className="px-4 py-2.5 text-red-500 text-sm font-medium rounded-full border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Guest Experience</h3>
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">
            This is what your guests see when they access concierge services through the mobile app.
          </p>
          <div className="flex justify-center" style={{ transform: "scale(0.85)", transformOrigin: "top center" }}>
            <IPhoneHomeScreen scale={0.45} />
          </div>
        </div>
      </div>

      {status.cancelAtPeriodEnd && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            Your subscription will remain active until{" "}
            <strong>
              {new Date(status.currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </strong>. After that, concierge services will be disabled for your guests.
          </p>
        </div>
      )}
    </div>
  );
}
